import React, { Component } from "react";

// Externals
import PropTypes from "prop-types";

// Material helpers
import { withStyles } from "@material-ui/core";

// Material components
import {
  Grid,
  Typography,
  Select,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Button,
} from "@material-ui/core";

import { FormControl, InputLabel } from "@material-ui/core";
import { Doughnut } from "react-chartjs-2";

import { Refresh as RefreshIcon } from "@material-ui/icons";

// Shared layouts
import { Dashboard as DashboardLayout } from "layouts";

// Custom components
import { TrendChart, ImpressionChart, Impressions } from "./components";

import compose from "recompose/compose";
import { connect } from "react-redux";
import { getCids } from "../../actions/authActions";
import api from "../../api";

import SelectBar from "react-select";

// Component styles
const styles = (theme) => ({
  root: {
    padding: theme.spacing.unit * 4,
  },
  item: {
    height: "100%",
  },
});

const colourStyles = {
  control: (styles) => ({ ...styles, fontFamily: "Roboto" }),
  option: (styles) => ({ ...styles, fontFamily: "Roboto" }),
};

const timeRangeList = [
  "Last Hour",
  "Last 24 Hours",
  "Last 7 Days",
  "Last 30 Days",
  "Previous Month",
];

const distributionKeyList = [
  { value: "os", label: "OS" },
  { value: "ua", label: "Platform" },
  { value: "pv", label: "Player Version" },
  { value: "av", label: "AV" },
];

const colors = [
  "#E12B17",
  "#E1AA17",
  "#CDF00E",
  "#17EEB3",
  "#39839B",
  "#2851C8",
  "#9719C0",
  "#D121A9",
  "#F31A20",
  "#5D6545",
];

const pointCnt = [
  60,
  24,
  168,
  720,
  getDaysInMonth(new Date().getFullYear(), new Date().getMonth()) * 24,
];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getTTS(timeRange, time) {
  if (timeRange !== 4) return undefined;

  const date = new Date(time);
  date.setUTCDate(1);
  date.setUTCHours(0);
  date.setUTCMinutes(0);
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);

  return Math.floor(date.getTime() / 1000) - 3600;
}
class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedOption: [],
      cidOption: [],
      timeRange: 0,
      lastUpdated: new Date(),
      distributionKey: "os",
      distribution: null, // raw { os|ua|pv|av: {k:v} }
      distributionPieData: null, // data compatible Doughnut
      distributionMeta: {
        withData: [],
        withoutData: [],
        coverageText: "",
      },
      data: {
        labels: [],
        datasets: [],
      },
      selectedTotalImpressions: 0,
    };
    this._reqSeq = 0; // latest-wins
    this._isMounted = false; // éviter setState après unmount
  }

  componentDidMount() {
    this.props.getCids(this.props.auth.user.cid);
    this._isMounted = true;

    // Debounce : si lodash est dispo, utilise lodash/debounce, sinon voir plus bas.
    this._reloadDebounced = this.debounce(this.reloadDashboard, 300);
  }
  componentWillUnmount() {
    this._isMounted = false;
    if (this._reloadDebounced?.cancel) this._reloadDebounced.cancel();
  }
  debounce = (fn, wait) => {
    let t = null;
    const debounced = (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
    debounced.cancel = () => {
      if (t) clearTimeout(t);
      t = null;
    };
    return debounced;
  };
  reloadDashboard = async () => {
    // latest-wins: capture un numéro unique pour CE chargement
    const seq = ++this._reqSeq;

    // (Optionnel) évite de relancer si le composant n’est plus monté
    if (!this._isMounted) return;

    // Mets le loader une seule fois par vague
    this.setState({ loading: true });

    try {
      // Lis les paramètres courants
      const { timeRange, distributionKey, selectedOption } = this.state;

      const allCids = this.props.auth.cids || [];
      const cids = selectedOption.map((opt) => allCids[opt.value]);

      const lastUpdated = new Date();

      // Idéalement: paralléliser data + distribution (si indépendant)
      const [chartData, distRes] = await Promise.all([
        this.requestData(timeRange, lastUpdated, cids),
        this.requestDistribution(timeRange, lastUpdated, distributionKey, cids),
      ]);

      // latest-wins: si une autre vague est partie après, on ignore
      if (seq !== this._reqSeq) return;
      if (!this._isMounted) return;
      let selectedTotalImpressions = 0;

      if (chartData && Array.isArray(chartData.datasets)) {
        chartData.datasets.forEach((ds) => {
          if (!Array.isArray(ds.data)) return;
          ds.data.forEach((v) => {
            const n = Number(v ?? 0);
            if (Number.isFinite(n)) {
              selectedTotalImpressions += n;
            }
          });
        });
      }

      this.setState({
        data: chartData,
        distribution: distRes.distribution,
        distributionPieData: distRes.distributionPieData,
        distributionMeta: distRes.distributionMeta || {
          withData: [],
          withoutData: [],
          coverageText: "",
        },
        lastUpdated,
        loading: false,
        selectedTotalImpressions,
      });
    } catch (err) {
      // latest-wins: ne pas afficher une erreur obsolète
      if (seq !== this._reqSeq) return;
      if (!this._isMounted) return;

      this.setState({
        loading: false,
        error: err?.message || "Failed to load dashboard data",
      });
    }
  };
  scheduleReload = () => {
    if (this._reloadDebounced) this._reloadDebounced();
    else this.reloadDashboard(); // fallback au cas où
  };

  componentDidUpdate(prevProps) {
    if (prevProps.auth.cids !== this.props.auth.cids) {
      const cids = this.props.auth.cids || [];

      const cidOption = cids.map((cid, index) => ({
        value: index,
        label: cid.name,
      }));

      this.setState(
        {
          cidOption,
          selectedOption: [], // ou une sélection par défaut si voulu
        },
        this.scheduleReload,
      );
    }
  }

  handleChange = (selectedOption) => {
    if (selectedOption && selectedOption.length > 10) return;

    this.setState(
      { selectedOption: selectedOption || [] },
      this.scheduleReload,
    );
  };

  handleChangeTimeRange = (event) => {
    const timeRange = parseInt(event.target.value, 10);
    this.setState({ timeRange }, this.scheduleReload);
  };

  handleRefresh = () => {
    this.reloadDashboard();
  };

  handleChangeDistributionKey = (event) => {
    const distributionKey = event.target.value;
    this.setState({ distributionKey }, this.scheduleReload);
  };

  getTS(timeRange, time) {
    let date = new Date(time);
    switch (timeRange) {
      case 0:
        return undefined;
      case 1:
        date.setHours(date.getHours() - 24);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return Math.floor(date.getTime() / 1000);
      case 2:
        date.setHours(date.getHours() - 168);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return Math.floor(date.getTime() / 1000);
      case 3:
        date.setHours(date.getHours() - 720);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return Math.floor(date.getTime() / 1000);
      case 4:
        date.setMonth(date.getMonth() - 1);
        date.setUTCDate(1);
        date.setUTCHours(0);
        date.setUTCMinutes(0);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
        return Math.floor(date.getTime() / 1000);
    }
  }
  getDistributionTS(timeRange, time) {
    // charts est agrégé à l’heure (ts aligné sur l’heure).
    // On aligne toujours sur l’heure et on recule selon la période.
    let date = new Date(time);

    if (timeRange === 0) {
      // "Last Hour": on prend la dernière heure complète
      date.setHours(date.getHours() - 1);
      date.setUTCMinutes(0);
      date.setUTCSeconds(0);
      date.setUTCMilliseconds(0);
      return Math.floor(date.getTime() / 1000);
    }

    // Sinon, on réutilise ta logique de range (24h/7j/30j/prev month)
    return this.getTS(timeRange, time);
  }

  buildPieDataFromDistributionMap(distMap) {
    // distMap: { "chrome": 123, "firefox": 7, ... }
    if (!distMap) return null;

    const entries = Object.entries(distMap)
      .map(([k, v]) => [k, parseInt(v, 10) || 0])
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);

    if (entries.length === 0) return null;

    // Optionnel: limiter à Top 10 + "Others"
    const MAX = 10;
    const top = entries.slice(0, MAX);
    const rest = entries.slice(MAX);

    let labels = top.map(([k]) => k);
    let values = top.map(([, v]) => v);

    if (rest.length > 0) {
      labels.push("Others");
      values.push(rest.reduce((acc, [, v]) => acc + v, 0));
    }

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
        },
      ],
    };
  }

  async requestDistribution(
    timeRange,
    lastUpdated,
    distributionKey,
    selectedCids,
  ) {
    const isLastHour = timeRange === 0;
    const fts = isLastHour
      ? undefined
      : this.getDistributionTS(timeRange, lastUpdated);
    const tts = isLastHour ? undefined : getTTS(timeRange, lastUpdated);
    const responses = await Promise.all(
      selectedCids.map((cid) =>
        api.getChartDistribution(
          cid.name,
          fts,
          tts,
          distributionKey,
          isLastHour ? "realtime" : undefined,
        ),
      ),
    );

    const globalMap = {};
    const withData = [];
    const withoutData = [];

    // On map responses <-> selectedCids (même ordre)
    responses.forEach((res, idx) => {
      const cidName = selectedCids[idx]?.name || `cid#${idx}`;
      const payload = res.data?.data?.[distributionKey] || {};

      // somme numérique des valeurs du payload
      const totalForCid = Object.values(payload).reduce((acc, v) => {
        const n = Number(v ?? 0);
        return acc + (Number.isFinite(n) ? n : 0);
      }, 0);

      if (!payload || totalForCid <= 0) {
        withoutData.push(cidName);
        return;
      }

      withData.push(cidName);

      // merge numérique (évite concat string)
      for (const k in payload) {
        const n = Number(payload[k] ?? 0);
        globalMap[k] = (globalMap[k] || 0) + (Number.isFinite(n) ? n : 0);
      }
    });

    const pie = this.buildPieDataFromDistributionMap(globalMap);

    // Texte de couverture pour badge
    const totalSelected = selectedCids.length;
    const covered = withData.length;
    const coverageText =
      totalSelected > 0 ? `${covered}/${totalSelected} CIDs` : `0/0 CIDs`;

    return {
      distribution: { [distributionKey]: globalMap },
      distributionPieData: pie,
      distributionMeta: { withData, withoutData, coverageText },
    };
  }

  createTimeStringMinute(diff, time) {
    let d = new Date(time);
    d.setSeconds(0);
    d.setMinutes(d.getMinutes() - diff);
    return d.getHours() + ":" + d.getMinutes();
  }

  createTimeStringHour(diff, time) {
    let d = new Date(time);
    d.setSeconds(0);
    d.setMinutes(0);
    d.setHours(d.getHours() - diff);
    return d.toLocaleDateString() + "-" + d.getHours();
  }

  createTimeStringDate(diff, time) {
    let d = new Date(time);
    d.setSeconds(0);
    d.setMinutes(0);
    d.setHours(d.getHours() - diff);
    return d.toLocaleDateString() + "-" + d.getHours();
  }

  getLabels(timeRange, time) {
    let count = pointCnt[timeRange];
    switch (timeRange) {
      case 0:
        return Array.from(Array(count).keys()).map((x) =>
          this.createTimeStringMinute(count - x, time),
        );
      case 1:
        return Array.from(Array(count).keys()).map((x) =>
          this.createTimeStringHour(count - x, time),
        );
      case 2:
      case 3:
        return Array.from(Array(count).keys()).map((x) =>
          this.createTimeStringDate(count - x, time),
        );
      case 4:
        return Array.from(Array(count).keys()).map((x) =>
          this.createTimeStringDate(
            count - x + time.getHours() + (time.getDate() - 1) * 24,
            time,
          ),
        );
    }
  }

  fillAndReverseData(data) {
    let { timeRange } = this.state;
    if (!data) return new Array(pointCnt[timeRange]).fill(0);
    if (pointCnt[timeRange] > data.length)
      data = data.concat(new Array(pointCnt[timeRange] - data.length).fill(0));
    else if (pointCnt[timeRange] < data.length)
      data = data.slice(data.length - pointCnt[timeRange]);
    return data.reverse().map((cnt) => {
      return isNaN(cnt) ? 0 : cnt;
    });
  }

  fillData(data) {
    let { timeRange } = this.state;
    if (!data) return new Array(pointCnt[timeRange]).fill(0);
    if (pointCnt[timeRange] > data.length)
      data = new Array(pointCnt[timeRange] - data.length).fill(0).concat(data);
    else if (pointCnt[timeRange] < data.length && timeRange != 4)
      data = data.slice(data.length - pointCnt[timeRange]);
    else if (pointCnt[timeRange] < data.length && timeRange == 4)
      // slice from head if timeRange is previous month
      data = data.slice(0, pointCnt[timeRange]);
    return data.map((cnt) => {
      return isNaN(cnt) ? 0 : cnt;
    });
  }

  async requestData(timeRange, lastUpdated, cids) {
    const ts = this.getTS(timeRange, lastUpdated);
    const tts = getTTS(timeRange, lastUpdated);

    const responses = await Promise.all(
      cids.map((cid) => api.getChartById(cid.name, ts, tts)),
    );

    return {
      labels: this.getLabels(timeRange, lastUpdated),
      datasets: responses.map((res, idx) => ({
        label: cids[idx].name,
        borderColor: colors[idx % colors.length],
        backgroundColor: colors[idx % colors.length],
        pointRadius: 1,
        fill: false,
        data: this.fillData(res.data.data),
      })),
    };
  }
  copyText = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {}
    // fallback
    try {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      return true;
    } catch (e) {
      return false;
    }
  };

  render() {
    const { classes } = this.props;
    const allCids = this.props.auth.cids;
    const cids = this.state.selectedOption.map((option) => {
      return allCids[option.value];
    });
    const {
      timeRange,
      lastUpdated,
      data,
      distributionKey,
      distributionPieData,
    } = this.state;
    return (
      <DashboardLayout title="Dashboard">
        <div className={classes.root}>
          <Grid container spacing={4}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Typography className={classes.title} variant="h4" gutterBottom>
                Customer ID Filter
              </Typography>
              <SelectBar
                styles={colourStyles}
                value={this.state.selectedOption}
                onChange={this.handleChange}
                options={this.state.cidOption}
                isMulti
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Select
                native
                value={timeRange}
                onChange={this.handleChangeTimeRange}
                inputProps={{
                  name: "timeRange",
                }}>
                <option value={0}>{timeRangeList[0]}</option>
                <option value={1}>{timeRangeList[1]}</option>
                <option value={2}>{timeRangeList[2]}</option>
                <option value={3}>{timeRangeList[3]}</option>
                <option value={4}>{timeRangeList[4]}</option>
              </Select>
              <FormControl>
                <InputLabel htmlFor="distributionKey">Distribution</InputLabel>
                <Select
                  native
                  value={distributionKey}
                  onChange={this.handleChangeDistributionKey}
                  inputProps={{
                    name: "distributionKey",
                    id: "distributionKey",
                  }}>
                  {distributionKeyList.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                className={classes.refreshButton}
                onClick={this.handleRefresh}
                variant="text">
                <RefreshIcon />
              </IconButton>
            </Grid>
            {/*
            <Grid item lg={3} sm={6} xl={3} xs={12}>
              <Impressions cids={cids}/>
            </Grid>
            */}
            {(() => {
              const total = Number(this.state.selectedTotalImpressions || 0);
              const formatted = total.toLocaleString("en-US");

              return (
                <Grid
                  container
                  alignItems="center"
                  spacing={2}
                  style={{ marginBottom: 12 }}>
                  <Grid item>
                    <Typography variant="body2" style={{ opacity: 0.8 }}>
                      Total impressions (current selection)
                    </Typography>
                    <Typography variant="h6" style={{ marginTop: 2 }}>
                      {formatted}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Tooltip title="Copy total impressions to clipboard">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          const ok = await this.copyText(String(total));
                          // Si tu as déjà un système de notif, branche ici.
                          if (!ok) console.warn("Copy failed");
                        }}>
                        Copy
                      </Button>
                    </Tooltip>
                  </Grid>

                  <Grid item style={{ minWidth: 220 }}>
                    <Typography
                      variant="caption"
                      style={{ display: "block", marginBottom: 4 }}>
                      Raw value
                    </Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={String(total)}
                      InputProps={{ readOnly: true }}
                      onFocus={(e) => e.target.select()}
                    />
                  </Grid>
                </Grid>
              );
            })()}
            <Grid item lg={12} md={12} xl={12} xs={12}>
              <TrendChart
                className={classes.item}
                lastUpdated={lastUpdated}
                cids={cids}
                timeRange={timeRange}
                data={data}
                title="Impressions for each customer ID"
              />
            </Grid>
            <Grid item lg={12} md={12} xl={12} xs={12}>
              {
                <ImpressionChart
                  data={data}
                  timeRange={timeRange}
                  lastUpdated={lastUpdated}
                  cids={cids}
                />
              }
            </Grid>
            <Grid item lg={12} md={12} xl={12} xs={12}>
              {(() => {
                const meta = this.state.distributionMeta || {
                  withData: [],
                  withoutData: [],
                  coverageText: "",
                };
                const hasPartial = (meta.withoutData?.length || 0) > 0;
                const label =
                  distributionKeyList.find((x) => x.value === distributionKey)
                    ?.label || distributionKey;
                const isLastHour = this.state.timeRange === 0;
                return (
                  <>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Typography variant="h6" gutterBottom>
                          Distribution by {label}
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Tooltip
                          title={
                            hasPartial
                              ? `Partial data: no ${label} info for ${meta.withoutData.join(", ")}`
                              : isLastHour
                                ? `Realtime ${label} data since last hourly aggregation (~:05).`
                                : `All selected CIDs provide ${label} data for current selection.`
                          }>
                          <Chip
                            size="small"
                            label={
                              hasPartial
                                ? `Partial data • ${meta.coverageText}`
                                : `OK • ${meta.coverageText}`
                            }
                            style={{
                              backgroundColor: hasPartial
                                ? "#fff3cd"
                                : "#d4edda",
                              color: "#1f2d3d",
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      </Grid>
                    </Grid>

                    <Typography
                      variant="body2"
                      style={{ marginBottom: 8, opacity: 0.8 }}>
                      {isLastHour ? (
                        <>
                          Distribution based on realtime data since the last
                          hourly aggregation (~:05).
                          <br />
                          Distribution includes only CIDs with available {
                            label
                          }{" "}
                          data.
                          <br />
                          Player data is available only since: EXO:
                          18767-master-2beb0dc2 • OSMP+/AV+: main 118008 •
                          HTML5+: 3.41.0-18791-master-913b378f
                        </>
                      ) : (
                        <>
                          Distribution includes only CIDs with available {label}{" "}
                          data.
                          <br />
                          Player data is available only since: EXO:
                          18767-master-2beb0dc2 • OSMP+/AV+: main 118008 •
                          HTML5+: 3.41.0-18791-master-913b378f
                        </>
                      )}
                    </Typography>

                    {hasPartial && (
                      <Typography variant="body2" style={{ marginBottom: 12 }}>
                        No {label} data for:{" "}
                        <b>{meta.withoutData.join(", ")}</b>
                      </Typography>
                    )}

                    {distributionPieData ? (
                      <Doughnut data={distributionPieData} />
                    ) : (
                      <Typography variant="body2">
                        No distribution data for the selected time range.
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Grid>
          </Grid>
        </div>
      </DashboardLayout>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  getCids: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, { getCids }),
)(Dashboard);
